import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { copyTextToClipboard } from '../../../../utils/utils';
import { EntityRequirement } from '../../../NeedInfoV2/CustomHooks/useFetchRequirements';

const REVERT_DELAY_MS = 2000;

/**
 * Utility function to check if data is empty
 */
const isEmptyData = (data: any) => {
    return !data || (Array.isArray(data) && data.length === 0);
};

/**
 * Writes rich HTML + plain text to the clipboard.
 * Falls back to plain text if ClipboardItem is not supported.
 */
const writeRichContent = async (html: string, text: string) => {
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
        await copyTextToClipboard(text);
        return;
    }
    await navigator.clipboard.write([
        new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
    ]);
};

/**
 * Custom hook to fetch and copy formatted text.
 * Also supports direct copy of plain text, DOM elements, and HTML content
 * via copyDirectText, copyElement, and copyHtmlContent.
 */
const useCopyText = (
    fetchData?: (pendingIds: Set<string>) => Promise<any>,
    formatData?: (data: any) => any,
    lenderRequirements?: EntityRequirement[],
) => {
    const [copiedText, setCopiedText] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [copiedId, setCopiedId] = useState<string | number | null>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const onCopySuccess = useCallback((id?: string | number) => {
        if (id == null) return;
        setCopiedId(id);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setCopiedId(null);
            timerRef.current = null;
        }, REVERT_DELAY_MS);
    }, []);

    /**
     * Fetches data via fetchData, formats it, and copies to clipboard.
     * Original API — used by NeedInfo and other existing consumers.
     */
    const copy = useCallback(async () => {
        if (!fetchData) return;
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const pendingIds = new Set<string>();
            lenderRequirements?.forEach((item) => {
                if (item.status === 'PENDING') {
                    pendingIds.add(item.id as string);
                }
            });
            const data = await fetchData(pendingIds);

            if (isEmptyData(data)) {
                setCopiedText('');
                toast.error('No data to copy');
                return;
            }

            const formattedText = formatData ? formatData(data) : typeof data === 'string' ? data : JSON.stringify(data, null, 2);

            await navigator.clipboard.writeText(formattedText);

            setCopiedText(formattedText);
            setSuccess(true);
            toast.success('Text copied to clipboard');
        } catch (err) {
            setCopiedText('');
            setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
            toast.error('Failed to copy text');
        } finally {
            setLoading(false);
        }
    }, [fetchData, formatData]);

    /**
     * Copies plain text directly to the clipboard.
     */
    const copyDirectText = useCallback(
        async (text: string, id: string | number) => {
            try {
                await copyTextToClipboard(text);
                onCopySuccess(id);
                toast.success('Copied to Clipboard');
            } catch {
                toast.error('Failed to copy');
            }
        },
        [onCopySuccess],
    );

    /**
     * Copies a DOM element's rendered content (HTML + plain text) to the clipboard.
     */
    const copyElement = useCallback(
        async (element: HTMLElement, id: string | number) => {
            try {
                await writeRichContent(element.innerHTML, element.innerText);
                onCopySuccess(id);
                toast.success('Copied to Clipboard');
            } catch {
                toast.error('Failed to copy');
            }
        },
        [onCopySuccess],
    );

    /**
     * Copies pre-built HTML and plain text to the clipboard.
     */
    const copyHtmlContent = useCallback(
        async (html: string, text: string, id: string | number) => {
            try {
                await writeRichContent(html, text);
                onCopySuccess(id);
                toast.success('Copied to Clipboard');
            } catch {
                toast.error('Failed to copy');
            }
        },
        [onCopySuccess],
    );

    return { copy, copiedText, loading, success, error, copiedId, copyDirectText, copyElement, copyHtmlContent };
};

export default useCopyText;
